import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  fetchBlogPostById,
  clearBlogDetail,
  deleteBlogPost,
} from '../../../Redux/Actions/actions';
import { categoryLabel } from '../../../constants/blog';
import MediaWatermark from '../../../Components/BlogMedia/MediaWatermark';
import PdfCanvasViewer from '../../../Components/BlogMedia/PdfCanvasViewer';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import styles from './BlogDetail.module.css';

const allowedBlog = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com',
];

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const BlogDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const post = useSelector((state) => state.blogDetail);
  const loading = useSelector((state) => state.blogLoading);
  const error = useSelector((state) => state.blogError);
  const user = useSelector((state) => state.user);

  const canManage = user?.user?.mail && allowedBlog.includes(user.user.mail);

  useEffect(() => {
    dispatch(fetchBlogPostById(id));
    return () => {
      dispatch(clearBlogDetail());
    };
  }, [dispatch, id]);

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta publicación? Esta acción no se puede deshacer.')) {
      return;
    }
    const res = await dispatch(deleteBlogPost(id));
    if (res?.success) {
      toast.success('Publicación eliminada.');
      navigate('/blog');
    } else {
      toast.error(res?.message || 'No se pudo eliminar la publicación.');
    }
  };

  if (loading && !post) {
    return <div className={styles.container}><p className={styles.info}>Cargando…</p></div>;
  }

  if (error && !post) {
    return (
      <div className={styles.container}>
        <p className={styles.error}>{error}</p>
        <Link to="/blog" className={styles.backLink}>← Volver al feed</Link>
      </div>
    );
  }

  if (!post) return null;

  const media = Array.isArray(post.media) ? [...post.media].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];

  const renderMedia = (item) => {
    if (item.type === 'pdf') {
      return <PdfCanvasViewer key={item.id} url={item.url} />;
    }
    return <MediaWatermark key={item.id} item={item} />;
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <Link to="/blog" className={styles.backLink}>← Volver al feed</Link>
        {canManage && (
          <div className={styles.adminActions}>
            <Link to={`/blog/editar/${post.id}`} className={styles.editBtn}>
              Editar
            </Link>
            <button type="button" className={styles.deleteBtn} onClick={handleDelete}>
              Eliminar
            </button>
          </div>
        )}
      </div>

      <article className={styles.article}>
        <header className={styles.articleHeader}>
          <span className={styles.category}>{categoryLabel(post.category)}</span>
          <h1 className={styles.title}>{post.title}</h1>
          <div className={styles.meta}>
            {post.authorName && <span>Por {post.authorName}</span>}
            <span>{formatDate(post.publishedAt || post.createdAt)}</span>
            {typeof post.views === 'number' && <span>{post.views} vistas</span>}
            {post.office?.name && <span>{post.office.name}</span>}
          </div>
          {Array.isArray(post.tags) && post.tags.length > 0 && (
            <div className={styles.tags}>
              {post.tags.map((tag) => (
                <span key={tag} className={styles.tag}>#{tag}</span>
              ))}
            </div>
          )}
        </header>

        {post.summary && <p className={styles.summary}>{post.summary}</p>}

        {media.length > 0 && (
          <section className={styles.mediaSection}>
            {media.map((item) => (
              <div key={item.id} className={styles.mediaBlock}>
                {item.title && <h4 className={styles.mediaTitle}>{item.title}</h4>}
                {renderMedia(item)}
              </div>
            ))}
          </section>
        )}

        {post.body && (
          <div
            className={styles.body}
            onContextMenu={(e) => e.preventDefault()}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body) }}
          />
        )}
      </article>
    </div>
  );
};

export default BlogDetail;
